import { useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { UserTableRow } from "@/components/team/UserTableRow";
import { ROLE_LABELS } from "@/lib/roles";

const EMPTY = { name: "", email: "", position: "", password: "", role: "employee" };

export default function ManagerUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.name} added`);
      setAddOpen(false);
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.name} deactivated` : `${u.name} activated`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRate = async (u, raw) => {
    const rate = raw === "" ? null : Number(raw);
    if (rate === (u.hourly_rate ?? null)) return;
    if (rate !== null && (Number.isNaN(rate) || rate < 0)) { toast.error("Enter a rate of 0 or more"); return; }
    try {
      await api.patch(`/admin/users/${u.id}`, { hourly_rate: rate });
      toast.success(rate === null ? `Rate cleared for ${u.name}` : `Rate saved for ${u.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${ROLE_LABELS[role]}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doReset = async () => {
    if (!resetFor) return;
    setSaving(true);
    try {
      await api.post(`/admin/users/${resetFor.id}/reset-password`, { password: newPw });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null);
      setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Team</h1>
          <p className="mt-1 text-muted-foreground">Create and manage contractor, approver and admin logins. Assign approvers in Admin.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="gap-2"><UserPlus className="h-4 w-4" /> Add Contractor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a new person</DialogTitle>
              <DialogDescription>They'll sign in with this email and password. Use their real email so reminders reach them.</DialogDescription>
            </DialogHeader>
            <form onSubmit={addUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="u-name">Full name</Label>
                <Input id="u-name" data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="u-email">Email</Label>
                  <Input id="u-email" data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-pos">Role / team</Label>
                  <Input id="u-pos" data-testid="user-position-input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="e.g. Consultant" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="u-pw">Temporary password</Label>
                  <Input id="u-pw" data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} placeholder="Min. 6 characters" />
                </div>
                <div className="space-y-2">
                  <Label>Access level</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Contractor</SelectItem>
                      <SelectItem value="manager">Approver</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-user-btn" type="submit" disabled={saving} className="gap-2">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create account
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mt-6 overflow-hidden" data-testid="team-view">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Hourly rate</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <UserTableRow key={u.id} u={u} isSelf={me?.id === u.id} onRole={changeRole} onToggleActive={toggleActive} onRate={changeRate}
                    onReset={(x) => { setResetFor(x); setNewPw(""); }} onDelete={deleteUser} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for {resetFor?.name}. Share it with them securely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-pw">New password</Label>
            <Input id="reset-pw" data-testid="reset-password-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={6} placeholder="Min. 6 characters" />
          </div>
          <DialogFooter>
            <Button data-testid="confirm-reset-btn" onClick={doReset} disabled={saving || newPw.length < 6} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
